import { useState, useCallback, useMemo } from "react";
import {
  ChevronRightIcon,
  ChevronDownIcon,
  FileIcon,
  FolderIcon,
  FolderOpenIcon,
  FileCode2Icon,
  FileJson2Icon,
  FileTextIcon,
  ImageIcon,
  FileTypeIcon,
  SearchIcon,
  XIcon,
  Loader2Icon,
} from "lucide-react";

import Message from "../../../../../i18n/Message";

import "./FileTree.scss";

// Get icon component based on file extension
const getFileIcon = (extension) => {
  const ext = (extension || "").toLowerCase();

  // Code files
  if (["js", "jsx", "ts", "tsx", "mjs", "cjs"].includes(ext)) {
    return FileCode2Icon;
  }

  // JSON files
  if (ext === "json") {
    return FileJson2Icon;
  }

  // Markdown/text files
  if (["md", "txt", "readme"].includes(ext)) {
    return FileTextIcon;
  }

  // Image files
  if (["png", "jpg", "jpeg", "gif", "svg", "webp", "ico", "bmp"].includes(ext)) {
    return ImageIcon;
  }

  // Config/style files
  if (["css", "scss", "sass", "less", "html", "xml", "yaml", "yml"].includes(ext)) {
    return FileTypeIcon;
  }

  return FileIcon;
};

const FileTreeNode = ({
  node,
  level = 0,
  onFileSelect,
  onFolderExpand,
  activeFilePath,
  expandedPaths,
  onToggleExpand,
}) => {
  const isFolder = node.type === "folder";
  const isExpanded = expandedPaths.has(node.path);
  const isSelected = activeFilePath === node.path;
  const hasChildren = node.children?.length > 0 || node.hasChildren;

  const handleClick = () => {
    if (isFolder) {
      onToggleExpand(node.path);
      // Load children if not already loaded
      if (!node.children && node.hasChildren && onFolderExpand) {
        onFolderExpand(node.path);
      }
    } else {
      onFileSelect?.(node);
    }
  };

  const IconComponent = isFolder ? null : getFileIcon(node.extension);

  return (
    <div className="tree_node">
      <div
        className={`node_content ${isSelected ? "selected" : ""}`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={handleClick}
      >
        {isFolder ? (
          <>
            <span className="expand_icon">
              {hasChildren ? (
                isExpanded ? (
                  <ChevronDownIcon size={14} />
                ) : (
                  <ChevronRightIcon size={14} />
                )
              ) : (
                <span style={{ width: 14 }} />
              )}
            </span>
            <span className="node_icon folder">
              {isExpanded ? <FolderOpenIcon size={16} /> : <FolderIcon size={16} />}
            </span>
          </>
        ) : (
          <>
            <span className="expand_icon" />
            <span className="node_icon file">
              <IconComponent size={16} />
            </span>
          </>
        )}
        <span className="node_name">{node.name}</span>
      </div>
      {isFolder && isExpanded && node.children && (
        <div className="children">
          {node.children.map((child) => (
            <FileTreeNode
              key={child.path}
              node={child}
              level={level + 1}
              onFileSelect={onFileSelect}
              onFolderExpand={onFolderExpand}
              activeFilePath={activeFilePath}
              expandedPaths={expandedPaths}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SearchResults = ({ results, onFileSelect, activeFilePath }) => {
  const { filenameMatches = [], contentMatches = [] } = results || {};
  const hasResults = filenameMatches.length > 0 || contentMatches.length > 0;

  if (!hasResults) {
    return (
      <div className="search_results empty">
        <p className="empty_text">
          <Message id="builder_no_search_results" defaultMessage="No results found" />
        </p>
      </div>
    );
  }

  return (
    <div className="search_results">
      {filenameMatches.length > 0 && (
        <div className="results_section">
          <div className="section_header">
            <Message id="builder_filename_matches" defaultMessage="File names" />
          </div>
          {filenameMatches.map((hit) => (
            <div
              key={hit.path}
              className={`result_item ${activeFilePath === hit.path ? "selected" : ""}`}
              onClick={() => onFileSelect({ path: hit.path, name: hit.name, type: "file" })}
            >
              <FileIcon size={14} />
              <span className="result_name">{hit.name}</span>
              <span className="result_path">{hit.path}</span>
            </div>
          ))}
        </div>
      )}
      {contentMatches.length > 0 && (
        <div className="results_section">
          <div className="section_header">
            <Message id="builder_content_matches" defaultMessage="Content matches" />
          </div>
          {contentMatches.map((hit, index) => (
            <div
              key={`${hit.path}-${hit.lineNumber}-${index}`}
              className={`result_item ${activeFilePath === hit.path ? "selected" : ""}`}
              onClick={() => onFileSelect({ path: hit.path, name: hit.name, type: "file" })}
            >
              <FileCode2Icon size={14} />
              <div className="result_content">
                <span className="result_name">{hit.name}:{hit.lineNumber}</span>
                <span className="result_context">{hit.matchContext}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FileTree = ({
  tree = [],
  loading,
  searchQuery,
  searchResults,
  searchLoading,
  onFolderExpand,
  onSearch,
  onSearchClear,
  onFileSelect,
  activeFilePath,
  expandedPaths = [],
  onToggleExpand,
}) => {
  const [localSearchQuery, setLocalSearchQuery] = useState("");

  // Convert array to Set for efficient lookup
  const expandedPathsSet = useMemo(() => new Set(expandedPaths), [expandedPaths]);

  // Debounced search
  const handleSearchChange = useCallback(
    (e) => {
      const query = e.target.value;
      setLocalSearchQuery(query);

      // Debounce search
      clearTimeout(window._searchTimeout);
      window._searchTimeout = setTimeout(() => {
        onSearch?.(query);
      }, 300);
    },
    [onSearch]
  );

  const handleSearchClear = useCallback(() => {
    setLocalSearchQuery("");
    onSearchClear?.();
  }, [onSearchClear]);

  const isSearching = localSearchQuery.length >= 2;

  return (
    <div className="file_tree">
      {/* Search box */}
      <div className="search_box">
        <SearchIcon size={16} className="search_icon" />
        <input
          type="text"
          placeholder="Search files..."
          value={localSearchQuery}
          onChange={handleSearchChange}
          className="search_input"
        />
        {localSearchQuery && (
          <button className="clear_button" onClick={handleSearchClear}>
            <XIcon size={14} />
          </button>
        )}
      </div>

      {/* Loading state */}
      {(loading || searchLoading) && (
        <div className="loading_state">
          <Loader2Icon size={20} className="spinning" />
        </div>
      )}

      {/* Search results or tree */}
      {isSearching ? (
        <SearchResults
          results={searchResults}
          onFileSelect={onFileSelect}
          activeFilePath={activeFilePath}
        />
      ) : !loading && (!tree || tree.length === 0) ? (
        <div className="empty_state">
          <p className="empty_text">
            <Message id="builder_no_sources" defaultMessage="No source files yet" />
          </p>
        </div>
      ) : (
        <div className="tree_content">
          {tree?.map((node) => (
            <FileTreeNode
              key={node.path}
              node={node}
              onFileSelect={onFileSelect}
              onFolderExpand={onFolderExpand}
              activeFilePath={activeFilePath}
              expandedPaths={expandedPathsSet}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FileTree;
